import { LinearGradient } from 'expo-linear-gradient'
import { useCallback, useEffect, useRef } from 'react'
import { useColorScheme } from 'nativewind'
import { Animated, type LayoutChangeEvent, Pressable, ScrollView, View } from 'react-native'

// Fallback hex colors for animation interpolation (PlatformColor can't be interpolated)
const COLORS = {
  light: { blue: '#007AFF', gray: '#F2F2F7', label: '#000000', white: '#FFFFFF', bg: '#FFFFFF', bgTransparent: '#FFFFFF00' },
  dark: { blue: '#0A84FF', gray: '#1C1C1E', label: '#FFFFFF', white: '#FFFFFF', bg: '#121212', bgTransparent: '#12121200' },
}

type Props<T extends string> = {
  tabs: { key: T; label: string }[]
  activeTag: T
  onSelect: (tag: T) => void
}

export default function PillFilterTabs<T extends string>({ tabs, activeTag, onSelect }: Props<T>) {
  const { colorScheme } = useColorScheme()
  const colors = colorScheme === 'dark' ? COLORS.dark : COLORS.light
  const scrollRef = useRef<ScrollView>(null)
  const tabLayouts = useRef<Record<string, { x: number; width: number }>>({})
  const scrollViewWidth = useRef(0)

  const scrollToTab = useCallback((key: string) => {
    const layout = tabLayouts.current[key]
    if (!layout || !scrollRef.current) return
    const offsetX = layout.x + layout.width / 2 - scrollViewWidth.current / 2
    scrollRef.current.scrollTo({ x: Math.max(0, offsetX), animated: true })
  }, [])

  const handleTabLayout = useCallback((key: string, e: LayoutChangeEvent) => {
    tabLayouts.current[key] = { x: e.nativeEvent.layout.x, width: e.nativeEvent.layout.width }
  }, [])

  return (
    <View className="pt-2 pb-2">
      <ScrollView
        ref={scrollRef}
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerClassName="px-4 pr-8 gap-2"
        onLayout={(e) => { scrollViewWidth.current = e.nativeEvent.layout.width }}
      >
        {tabs.map(tab => (
          <PillTab
            key={tab.key}
            label={tab.label}
            isActive={tab.key === activeTag}
            activeColor={colors.blue}
            inactiveColor={colors.gray}
            activeLabelColor={colors.white}
            inactiveLabelColor={colors.label}
            onPress={() => {
              onSelect(tab.key)
              scrollToTab(tab.key)
            }}
            onLayout={(e) => handleTabLayout(tab.key, e)}
          />
        ))}
      </ScrollView>
      <LinearGradient
        colors={[colors.bgTransparent, colors.bg]}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 0 }}
        pointerEvents="none"
        style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: 32 }}
      />
    </View>
  )
}

function PillTab({
  label,
  isActive,
  activeColor,
  inactiveColor,
  activeLabelColor,
  inactiveLabelColor,
  onPress,
  onLayout,
}: {
  label: string
  isActive: boolean
  activeColor: string
  inactiveColor: string
  activeLabelColor: string
  inactiveLabelColor: string
  onPress: () => void
  onLayout?: (e: LayoutChangeEvent) => void
}) {
  const anim = useRef(new Animated.Value(isActive ? 1 : 0)).current

  useEffect(() => {
    Animated.timing(anim, {
      toValue: isActive ? 1 : 0,
      duration: 250,
      useNativeDriver: false,
    }).start()
  }, [isActive, anim])

  const backgroundColor = anim.interpolate({
    inputRange: [0, 1],
    outputRange: [inactiveColor, activeColor],
  })

  const color = anim.interpolate({
    inputRange: [0, 1],
    outputRange: [inactiveLabelColor, activeLabelColor],
  })

  return (
    <Pressable onPress={onPress} onLayout={onLayout}>
      <Animated.View
        style={{
          backgroundColor,
          paddingHorizontal: 16,
          paddingVertical: 8,
          borderRadius: 999,
        }}
      >
        <Animated.Text
          style={{
            color,
            fontSize: 15,
            fontWeight: '600',
          }}
        >
          {label}
        </Animated.Text>
      </Animated.View>
    </Pressable>
  )
}
